import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Typography,
  Divider,
} from "@material-ui/core";
import {
  Publish,
  GetApp,
  CheckCircleOutline,
  ErrorOutline,
  Image as ImageIcon,
  Description,
} from "@material-ui/icons";

import { parseAffirmationCsv, runBulkImport, validateRows } from "./bulkImport";
import { downloadCsv } from "../../utils/downloadCsv";
import notice from "../../utils/notice";

const readFileText = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });

// A styled MUI file picker (native <input> is visually inconsistent with the
// rest of the UI, so we hide it behind a Button-as-label).
const FilePicker = ({ id, accept, multiple, disabled, icon, label, onChange }) => (
  <Button
    component="label"
    htmlFor={id}
    variant="outlined"
    color="default"
    size="small"
    startIcon={icon}
    disabled={disabled}
  >
    {label}
    <input
      id={id}
      type="file"
      accept={accept}
      multiple={multiple}
      hidden
      disabled={disabled}
      onChange={onChange}
    />
  </Button>
);

// Bulk-create Zone In affirmations from a CSV (copy + image file names)
// plus the image files. Reuses the single-create endpoint per row, so the
// backend handles the S3 upload — no new API needed.
const BulkZoneInModal = ({ open, onClose, onComplete }) => {
  const [csvFile, setCsvFile] = useState(null);
  const [imageFiles, setImageFiles] = useState([]);
  const [preview, setPreview] = useState(null); // { total, ready, problems, unusedImages }
  const [parseError, setParseError] = useState("");
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState(null);
  const cancelRef = useRef(false);

  const reset = () => {
    setCsvFile(null);
    setImageFiles([]);
    setPreview(null);
    setParseError("");
    setRunning(false);
    setProgress({ done: 0, total: 0 });
    setResult(null);
    cancelRef.current = false;
  };

  const close = () => {
    if (running) return;
    reset();
    onClose();
  };

  // Re-parse + re-validate whenever the CSV or the image set changes, so the
  // preview always reflects the current selection (before any upload).
  const recompute = async (file, images) => {
    setResult(null);
    setParseError("");
    if (!file) {
      setPreview(null);
      return;
    }
    try {
      const text = await readFileText(file);
      const rows = parseAffirmationCsv(text);
      if (rows.length === 0) {
        setPreview(null);
        setParseError("No data rows found in the CSV.");
        return;
      }
      // Fail fast on a schema problem with one clear message, rather than
      // emitting a per-row error for every row. Row objects are keyed by the
      // CSV headers, so a missing required column is absent from rows[0].
      const missingCols = ["copy", "picture_1"].filter((c) => !(c in rows[0]));
      if (missingCols.length) {
        setPreview(null);
        setParseError(
          `CSV is missing required column(s): ${missingCols.join(", ")}.`
        );
        return;
      }
      setPreview({ rows, ...validateRows(rows, images) });
    } catch (e) {
      setPreview(null);
      setParseError("Could not read the CSV file.");
    }
  };

  const onCsvChange = (e) => {
    const file = e.target.files[0] || null;
    setCsvFile(file);
    recompute(file, imageFiles);
  };

  const onImagesChange = (e) => {
    const images = e.target.files;
    setImageFiles(images);
    recompute(csvFile, images);
  };

  const downloadTemplate = () => {
    downloadCsv(
      ["copy", "picture_1", "picture_2", "is_pro_athlete"],
      [
        ["You are stronger than you think.", "affirmation1.jpg", "", "false"],
        ["Champions are made in practice.", "affirmation2.jpg", "alt2.jpg", "true"],
      ],
      "zonein_affirmations_template.csv"
    );
  };

  const run = async () => {
    if (running) return; // re-entry guard: never start a second concurrent run
    if (!preview || preview.ready === 0) return;
    try {
      setRunning(true);
      setResult(null);
      cancelRef.current = false;
      setProgress({ done: 0, total: preview.rows.length });
      const res = await runBulkImport({
        rows: preview.rows,
        files: imageFiles,
        onProgress: (done, total) => setProgress({ done, total }),
        shouldCancel: () => cancelRef.current,
      });
      setResult(res);
      const tone = res.errors.length || res.cancelled ? undefined : "success";
      notice(
        `${res.cancelled ? "Cancelled — " : ""}created ${res.created} of ${preview.rows.length}`,
        tone
      );
      if (res.created > 0 && onComplete) onComplete();
    } catch (e) {
      notice(`Bulk import failed${e?.message ? `: ${e.message}` : ""}`);
    } finally {
      setRunning(false);
    }
  };

  // No re-run once a result is shown — re-importing would create duplicates
  // (the backend doesn't dedup). To go again, close and reopen with a fresh CSV.
  const canRun = !!preview && preview.ready > 0 && !running && !result;

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="sm"
      id="zonein_bulk_dialog"
    >
      <DialogTitle>Bulk upload Zone In affirmations</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Upload a CSV with columns <b>copy</b>, <b>picture_1</b>, and
          optionally <b>picture_2</b> — the picture columns hold the image{" "}
          <b>file names</b>. Then select all the image files. The importer
          uploads the images and creates each affirmation.
        </Typography>

        <Button
          onClick={downloadTemplate}
          size="small"
          color="primary"
          startIcon={<GetApp />}
          style={{ marginTop: 4, marginBottom: 8 }}
        >
          Download CSV template
        </Button>

        <Divider />

        <div style={{ marginTop: 16 }}>
          <Typography variant="subtitle2" gutterBottom>
            1. Spreadsheet (CSV)
          </Typography>
          <FilePicker
            id="zonein_bulk_csv"
            accept=".csv,text/csv"
            disabled={running}
            icon={<Description />}
            label="Choose CSV"
            onChange={onCsvChange}
          />
          {csvFile && (
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ marginLeft: 12 }}
            >
              {csvFile.name}
            </Typography>
          )}
        </div>

        <div style={{ marginTop: 16 }}>
          <Typography variant="subtitle2" gutterBottom>
            2. Image files
          </Typography>
          <FilePicker
            id="zonein_bulk_images"
            accept="image/*"
            multiple
            disabled={running}
            icon={<ImageIcon />}
            label="Choose images"
            onChange={onImagesChange}
          />
          {imageFiles.length > 0 && (
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ marginLeft: 12 }}
            >
              {imageFiles.length} image(s) selected
            </Typography>
          )}
        </div>

        {parseError && (
          <Typography variant="caption" color="error" component="p" style={{ marginTop: 16 }}>
            {parseError}
          </Typography>
        )}

        {preview && !running && !result && (
          <div style={{ marginTop: 20 }}>
            <Divider />
            <Typography variant="subtitle2" style={{ marginTop: 12 }}>
              Preview
            </Typography>
            <Typography variant="body2" style={{ display: "flex", alignItems: "center", marginTop: 4 }}>
              <CheckCircleOutline
                fontSize="small"
                style={{ color: "#1bc5bd", marginRight: 6 }}
              />
              {preview.ready} of {preview.total} row(s) ready to import
            </Typography>

            {preview.problems.length > 0 && (
              <>
                <Typography
                  variant="body2"
                  color="error"
                  style={{ display: "flex", alignItems: "center", marginTop: 4 }}
                >
                  <ErrorOutline fontSize="small" style={{ marginRight: 6 }} />
                  {preview.problems.length} row(s) will be skipped:
                </Typography>
                <ul style={{ maxHeight: 140, overflow: "auto", marginTop: 4 }}>
                  {preview.problems.map((p, idx) => (
                    <li key={idx}>
                      <Typography variant="caption" color="error">
                        Row {p.row}: {p.message}
                      </Typography>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {preview.duplicateImages.length > 0 && (
              <Typography
                variant="caption"
                color="error"
                component="p"
                style={{ marginTop: 4 }}
              >
                Duplicate file name(s) — only one will be used, rename to
                disambiguate: {preview.duplicateImages.slice(0, 5).join(", ")}
                {preview.duplicateImages.length > 5 ? "…" : ""}
              </Typography>
            )}

            {preview.unusedImages.length > 0 && (
              <Typography
                variant="caption"
                color="textSecondary"
                component="p"
                style={{ marginTop: 4 }}
              >
                {preview.unusedImages.length} selected image(s) not referenced by any
                row: {preview.unusedImages.slice(0, 5).join(", ")}
                {preview.unusedImages.length > 5 ? "…" : ""}
              </Typography>
            )}
          </div>
        )}

        {running && (
          <div style={{ marginTop: 20 }}>
            <Typography variant="caption">
              Uploading {progress.done} / {progress.total}…
            </Typography>
            <LinearProgress
              variant="determinate"
              value={
                progress.total ? (progress.done / progress.total) * 100 : 0
              }
            />
          </div>
        )}

        {result && (
          <div style={{ marginTop: 20 }}>
            {result.errors.length === 0 && !result.cancelled ? (
              <Typography
                variant="subtitle2"
                style={{ display: "flex", alignItems: "center", color: "#1bc5bd" }}
              >
                <CheckCircleOutline fontSize="small" style={{ marginRight: 6 }} />
                Done — created all {result.created} affirmation
                {result.created === 1 ? "" : "s"}.
              </Typography>
            ) : (
              <Typography variant="subtitle2">
                {result.cancelled ? "Cancelled — " : ""}Created {result.created},{" "}
                {result.errors.length} failed
              </Typography>
            )}
            {result.errors.length > 0 && (
              <ul style={{ maxHeight: 160, overflow: "auto", marginTop: 8 }}>
                {result.errors.map((er, idx) => (
                  <li key={idx}>
                    <Typography variant="caption" color="error">
                      Row {er.row}: {er.message}
                    </Typography>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        {running ? (
          <>
            <Button
              onClick={() => {
                cancelRef.current = true;
              }}
              color="secondary"
            >
              Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              startIcon={<Publish />}
              disabled
            >
              Importing…
            </Button>
          </>
        ) : result ? (
          // Import finished — only a primary Done remains; re-importing here
          // would duplicate, so the run button is gone until the modal reopens.
          <Button onClick={close} color="primary" variant="contained">
            Done
          </Button>
        ) : (
          <>
            <Button onClick={close} color="secondary">
              Close
            </Button>
            <Button
              onClick={run}
              color="primary"
              variant="contained"
              startIcon={<Publish />}
              disabled={!canRun}
            >
              {preview && preview.ready > 0
                ? `Import ${preview.ready}`
                : "Import"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

BulkZoneInModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
};

export default BulkZoneInModal;
